import * as THREE from 'three'
import { Arena } from '../world/Arena'
import { BALANCE } from '../config'
import {
  advanceAssaultBoost,
  advanceEnergy,
  advanceStagger,
  applyStagger,
  clamp,
  type AssaultBoostState,
  type ResourceState,
  type StaggerState,
} from '../mechanics'
import { animateMech, createMech, type MechRig } from '../render/MechFactory'
import type { InputManager } from '../input/InputManager'

export interface PlayerFrameState {
  boosting: boolean
  assaultBoosting: boolean
  quickBoosted: boolean
  airborne: boolean
  planarSpeed: number
  collided: boolean
}

export class PlayerMech {
  readonly rig: MechRig
  readonly velocity = new THREE.Vector3()
  readonly position = new THREE.Vector3(0, 0, 28)
  armor: number = BALANCE.player.maxAp
  energy: ResourceState = { value: BALANCE.player.maxEnergy, rechargeDelay: 0 }
  stagger: StaggerState = { value: 0, overloadedFor: 0 }
  private verticalVelocity = 0
  private quickBoostRemaining = 0
  private quickBoostCooldown = 0
  private quickBoostDirection = new THREE.Vector3()
  private assault: AssaultBoostState = { charge: 0, engaged: false }
  private recoil = 0
  private flinch = 0
  private flinchYaw = 0
  private structureContact = false
  /** Vertical velocity at the frame the mech last touched down, for the landing crouch. */
  private landingVelocity = 0

  constructor(scene: THREE.Scene, private readonly arena: Arena) {
    this.rig = createMech({ faction: 'player' })
    this.rig.root.position.copy(this.position)
    scene.add(this.rig.root)
  }

  get alive(): boolean {
    return this.armor > 0
  }

  get aimPoint(): THREE.Vector3 {
    return this.position.clone().add(new THREE.Vector3(0, 4.8, 0))
  }

  get collided(): boolean {
    return this.structureContact
  }

  kickRecoil(amount = 0.2): void {
    this.recoil = Math.max(this.recoil, amount)
  }

  update(
    deltaSeconds: number,
    time: number,
    input: InputManager,
    cameraYaw: number,
    facingTarget: THREE.Vector3 | null,
  ): PlayerFrameState {
    this.quickBoostCooldown = Math.max(0, this.quickBoostCooldown - deltaSeconds)
    this.quickBoostRemaining = Math.max(0, this.quickBoostRemaining - deltaSeconds)
    this.recoil *= Math.exp(-15 * deltaSeconds)
    this.flinch *= Math.exp(-9 * deltaSeconds)
    this.landingVelocity *= Math.exp(-7 * deltaSeconds)
    this.stagger = advanceStagger(this.stagger, deltaSeconds, 16)

    const forwardAxis = Number(input.isDown('KeyW') || input.isDown('ArrowUp')) - Number(input.isDown('KeyS') || input.isDown('ArrowDown'))
    const rightAxis = Number(input.isDown('KeyD') || input.isDown('ArrowRight')) - Number(input.isDown('KeyA') || input.isDown('ArrowLeft'))
    const cameraForward = new THREE.Vector3(Math.sin(cameraYaw), 0, -Math.cos(cameraYaw))
    const cameraRight = new THREE.Vector3(Math.cos(cameraYaw), 0, Math.sin(cameraYaw))
    const move = cameraForward.multiplyScalar(forwardAxis).add(cameraRight.clone().multiplyScalar(rightAxis * BALANCE.player.strafeSpeed))
    if (move.lengthSq() > 1) move.normalize()

    const hasInput = move.lengthSq() > 0.001
    const wantsBoost = input.isDown('ShiftLeft') || input.isDown('ShiftRight')
    const boosting = wantsBoost && hasInput && this.energy.value > 1 && this.stagger.overloadedFor === 0
    let quickBoosted = false

    const quickLeft = input.wasPressed('KeyQ')
    const quickRight = input.wasPressed('KeyE')
    if (
      (quickLeft || quickRight) &&
      this.quickBoostCooldown === 0 &&
      this.energy.value >= BALANCE.player.quickBoostCost &&
      this.stagger.overloadedFor === 0
    ) {
      const direction = quickLeft ? -1 : 1
      this.quickBoostDirection.copy(cameraRight).multiplyScalar(direction)
      if (hasInput) this.quickBoostDirection.add(move.clone().multiplyScalar(0.55)).normalize()
      this.quickBoostRemaining = BALANCE.player.quickBoostDuration
      this.quickBoostCooldown = BALANCE.player.quickBoostCooldown
      this.energy = {
        value: this.energy.value - BALANCE.player.quickBoostCost,
        rechargeDelay: BALANCE.player.energyRechargeDelay,
      }
      quickBoosted = true
    }

    // Assault boost escalates out of a held forward boost. `forwardAxis > 0`
    // rather than `hasInput`: strafing into a sprint would make it impossible
    // to reposition at speed without committing.
    this.assault = advanceAssaultBoost(this.assault, deltaSeconds, {
      boosting,
      forward: forwardAxis > 0,
      energy: this.energy.value,
      disrupted: this.stagger.overloadedFor > 0 || this.structureContact,
    })
    const assaultBoosting = this.assault.engaged

    const verticalBoost = input.isDown('Space') && this.energy.value > 0.5 && this.stagger.overloadedFor === 0
    const drain =
      (assaultBoosting
        ? BALANCE.player.assaultBoostDrainPerSecond
        : boosting
          ? BALANCE.player.assaultDrainPerSecond
          : 0) +
      (verticalBoost ? BALANCE.player.verticalDrainPerSecond : 0)
    this.energy = advanceEnergy(
      this.energy,
      deltaSeconds,
      drain,
      BALANCE.player.energyRechargePerSecond,
      BALANCE.player.energyRechargeDelay,
      BALANCE.player.maxEnergy,
    )

    if (this.stagger.overloadedFor > 0) {
      this.velocity.multiplyScalar(Math.exp(-9 * deltaSeconds))
    } else if (this.quickBoostRemaining > 0) {
      this.velocity.lerp(this.quickBoostDirection.clone().multiplyScalar(BALANCE.player.quickBoostSpeed), 0.42)
    } else {
      const speed = assaultBoosting
        ? BALANCE.player.assaultBoostSpeed
        : boosting
          ? BALANCE.player.boostSpeed
          : BALANCE.player.walkSpeed
      const desired = hasInput ? move.multiplyScalar(speed) : new THREE.Vector3()
      const acceleration = assaultBoosting
        ? BALANCE.player.assaultBoostAcceleration
        : BALANCE.player.acceleration
      const blend = 1 - Math.exp(-acceleration * deltaSeconds / Math.max(1, speed))
      this.velocity.lerp(desired, blend)
      if (!hasInput) this.velocity.multiplyScalar(Math.exp(-BALANCE.player.groundDrag * deltaSeconds))
    }

    if (verticalBoost) {
      this.verticalVelocity = Math.min(12, this.verticalVelocity + 28 * deltaSeconds)
    } else {
      this.verticalVelocity -= BALANCE.player.gravity * deltaSeconds
    }
    const wasAirborne = this.position.y > 0.05
    this.position.y += this.verticalVelocity * deltaSeconds
    if (this.position.y < 0) {
      this.position.y = 0
      // Capture the descent speed before it is zeroed: the crouch depth is
      // driven by how hard the landing was, not by the frame after it.
      if (wasAirborne) this.landingVelocity = Math.min(this.landingVelocity, this.verticalVelocity)
      this.verticalVelocity = Math.max(0, this.verticalVelocity)
    }

    const previousPosition = this.position.clone()
    this.position.addScaledVector(this.velocity, deltaSeconds)
    this.structureContact = this.arena.resolveBody(this.position, 2.25, 5.8, previousPosition)
    if (this.structureContact) this.velocity.multiplyScalar(0.2)

    const faceDirection = facingTarget
      ? facingTarget.clone().sub(this.position).setY(0)
      : this.velocity.clone().setY(0)
    if (faceDirection.lengthSq() > 0.05) {
      const desiredYaw = Math.atan2(faceDirection.x, -faceDirection.z)
      const authority = 14 * (assaultBoosting ? BALANCE.player.assaultBoostTurnFactor : 1)
      this.rig.root.rotation.y = dampAngle(this.rig.root.rotation.y, desiredYaw, authority, deltaSeconds)
    }

    this.rig.root.position.copy(this.position)
    const planarSpeed = Math.hypot(this.velocity.x, this.velocity.z)
    animateMech(this.rig, {
      time,
      planarSpeed,
      boosting: boosting || this.quickBoostRemaining > 0 || verticalBoost,
      overloaded: this.stagger.overloadedFor > 0,
      airborne: this.position.y > 0.05,
      verticalVelocity: this.landingVelocity,
      recoil: this.recoil,
      flinch: this.flinch,
      flinchYaw: this.flinchYaw,
    })

    return {
      boosting: boosting || verticalBoost,
      assaultBoosting,
      quickBoosted,
      airborne: this.position.y > 0.05,
      planarSpeed,
      collided: this.structureContact,
    }
  }

  applyDamage(
    damage: number,
    impact: number,
    source?: THREE.Vector3,
  ): { destroyed: boolean; overloaded: boolean } {
    const wasOverloaded = this.stagger.overloadedFor > 0
    const multiplier = wasOverloaded ? 1.45 : 1
    this.armor = clamp(this.armor - damage * multiplier, 0, BALANCE.player.maxAp)
    this.stagger = applyStagger(this.stagger, impact)
    // Flinch away from the shooter, in the mech's own frame, so a hit from the
    // left rolls the torso right rather than always pitching it back.
    this.flinch = Math.min(1, this.flinch + clamp(impact / 26, 0.12, 0.7))
    if (source) {
      const incoming = source.clone().sub(this.position).setY(0)
      if (incoming.lengthSq() > 0.0001) {
        this.flinchYaw = Math.atan2(incoming.x, -incoming.z) - this.rig.root.rotation.y
      }
    }
    return { destroyed: this.armor <= 0, overloaded: !wasOverloaded && this.stagger.overloadedFor > 0 }
  }

  /** Direction the last hit arrived from, in mech-local radians. For the HUD. */
  get lastHitYaw(): number {
    return this.flinchYaw
  }

  get flinchStrength(): number {
    return this.flinch
  }
}

function dampAngle(current: number, target: number, speed: number, deltaSeconds: number): number {
  const difference = Math.atan2(Math.sin(target - current), Math.cos(target - current))
  return current + difference * (1 - Math.exp(-speed * deltaSeconds))
}
